import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { cn } from '../lib/ui'
import { NotificationsList } from './NotificationsList'

type Props = {
  className?: string
}

export function NotificationsBell({ className }: Props) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)

  const handleUnreadChange = useCallback((count: number) => {
    setUnread(count)
  }, [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  if (!user?.id) return null

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <button
        type="button"
        className={cn(
          'relative inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-su-border',
          'bg-su-surface text-su-text-strong shadow-su-sm transition hover:bg-su-surface-muted',
        )}
        aria-label={unread > 0 ? `Уведомления: ${unread} непрочитанных` : 'Уведомления'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 ? (
          <span
            className={cn(
              'absolute -right-1 -top-1 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center',
              'rounded-full bg-red-500 px-1 text-[0.625rem] font-bold leading-none text-white',
            )}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className={cn(
            'absolute right-0 top-[calc(100%+0.5rem)] z-[10050] w-[min(22rem,calc(100vw-2rem))]',
            'overflow-hidden rounded-su-lg border border-su-border bg-su-surface shadow-su-md',
          )}
        >
          <div className="border-b border-su-border bg-su-surface-muted px-3 py-2.5">
            <h3 className="m-0 text-sm font-semibold text-su-text-strong">Уведомления</h3>
          </div>
          <NotificationsList
            recipientId={user.id}
            compact
            onUnreadChange={handleUnreadChange}
          />
        </div>
      ) : null}
    </div>
  )
}
